import { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, UserCog } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FootballPage = () => {
  const [players, setPlayers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [activeTeam, setActiveTeam] = useState('Seniores Masculinos');
  const [loading, setLoading] = useState(true);

  const teams = ['Seniores Masculinos', 'Seniores Femininos', 'Formação'];

  useEffect(() => {
    fetchData();
  }, [activeTeam]);

  const fetchData = async () => {
    try {
      const [playersRes, staffRes] = await Promise.all([
        axios.get(`${API}/players?team=${activeTeam}`),
        axios.get(`${API}/players/staff`),
      ]);
      setPlayers(playersRes.data);
      setStaff(staffRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const positions = {
    'Guarda-Redes': players.filter(p => p.position === 'Guarda-Redes'),
    'Defesa': players.filter(p => p.position === 'Defesa'),
    'Médio': players.filter(p => p.position === 'Médio'),
    'Avançado': players.filter(p => p.position === 'Avançado'),
  };

  return (
    <div className="min-h-screen pt-20" data-testid="football-page">
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1517466787929-bc90951d0974?auto=format&fit=crop&q=80')`,
          }}
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 text-center section-padding">
          <span className="text-xs uppercase tracking-widest text-gray-400 font-bold">Época 2024/2025</span>
          <h1 className="font-heading text-5xl md:text-7xl font-black mt-2">FUTEBOL</h1>
        </div>
      </section>

      {/* Team Selector */}
      <section className="bg-[#0A0A0A] border-b border-white/10">
        <div className="container-custom section-padding">
          <div className="flex flex-wrap gap-4 py-6">
            {teams.map((team) => (
              <button
                key={team}
                onClick={() => setActiveTeam(team)}
                className={`px-6 py-3 text-xs uppercase tracking-widest font-bold transition-all ${
                  activeTeam === team
                    ? 'bg-white text-black'
                    : 'border border-white/20 text-gray-400 hover:text-white hover:border-white/40'
                }`}
                data-testid={`team-btn-${team.toLowerCase().replace(' ', '-')}`}
              >
                {team}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Squad Section */}
      <section className="py-24 section-padding" data-testid="squad-section">
        <div className="container-custom">
          <div className="flex items-center gap-3 mb-12">
            <Users size={24} />
            <h2 className="font-heading text-3xl font-black">PLANTEL</h2>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-[#0A0A0A] border border-white/10 h-80 skeleton" />
              ))}
            </div>
          ) : players.length > 0 ? (
            <div className="space-y-16">
              {Object.entries(positions).map(([position, positionPlayers]) => 
                positionPlayers.length > 0 && (
                  <div key={position}>
                    <h3 className="font-heading text-xl font-bold mb-6 text-gray-400">{position.toUpperCase()}</h3>
                    <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
                      {positionPlayers.map((player) => (
                        <div key={player.id} className="player-card" data-testid={`player-${player.id}`}>
                          <div className="aspect-[3/4] relative overflow-hidden bg-[#0A0A0A]">
                            {player.photo_url ? (
                              <img
                                src={player.photo_url}
                                alt={player.name}
                                className="player-image w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-white/5">
                                <span className="font-heading text-6xl font-black text-white/10">
                                  {player.number}
                                </span>
                              </div>
                            )}
                            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 to-transparent">
                              <div className="flex items-end justify-between">
                                <div>
                                  <p className="font-heading font-bold text-lg">{player.name}</p>
                                  <p className="text-gray-400 text-xs uppercase tracking-wider">{player.position}</p>
                                </div>
                                <span className="font-heading text-4xl font-black text-white/20">
                                  {player.number}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              )}
            </div>
          ) : (
            <div className="text-center py-16 border border-white/10">
              <Users size={48} className="mx-auto mb-4 text-gray-600" />
              <p className="text-gray-500">Plantel em atualização</p>
              <p className="text-gray-600 text-sm mt-2">Os jogadores serão adicionados em breve</p>
            </div>
          )}
        </div>
      </section>

      {/* Staff Section */}
      <section className="py-24 bg-[#0A0A0A] border-y border-white/10" data-testid="staff-section">
        <div className="container-custom section-padding">
          <div className="flex items-center gap-3 mb-12">
            <UserCog size={24} />
            <h2 className="font-heading text-3xl font-black">EQUIPA TÉCNICA</h2>
          </div>

          {staff.length > 0 ? (
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
              {staff.map((member) => (
                <div key={member.id} className="border border-white/10 p-6 text-center hover:border-white/30 transition-colors">
                  <div className="w-24 h-24 mx-auto mb-4 bg-white/10 rounded-full overflow-hidden">
                    {member.photo_url ? (
                      <img src={member.photo_url} alt={member.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="font-heading font-bold text-2xl text-gray-400">
                          {member.name.charAt(0)}
                        </span>
                      </div>
                    )}
                  </div>
                  <h3 className="font-heading font-bold text-lg">{member.name}</h3>
                  <p className="text-gray-400 text-sm mt-1">{member.staff_role}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 border border-white/10">
              <UserCog size={48} className="mx-auto mb-4 text-gray-600" />
              <p className="text-gray-500">Equipa técnica em atualização</p>
            </div>
          )}
        </div>
      </section>

      {/* Teams Overview */}
      <section className="py-24 section-padding" data-testid="teams-overview">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-widest text-gray-400 font-bold">As Nossas Equipas</span>
            <h2 className="font-heading text-4xl md:text-5xl font-black mt-2">MODALIDADES</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="border border-white/10 p-8 hover:border-white/30 transition-colors">
              <h3 className="font-heading text-2xl font-bold mb-4">SENIORES MASCULINOS</h3>
              <p className="text-gray-400 text-sm mb-4">
                Equipa principal do clube, atualmente a competir na 1ª Divisão da Associação de Futebol de Braga.
              </p>
              <span className="text-xs uppercase tracking-widest text-gray-500">1ª Divisão AF Braga</span>
            </div>
            <div className="border border-white/10 p-8 hover:border-white/30 transition-colors">
              <h3 className="font-heading text-2xl font-bold mb-4">SENIORES FEMININOS</h3>
              <p className="text-gray-400 text-sm mb-4">
                Equipa feminina de futsal, a competir na 1ª Divisão de Futsal Feminino da AF Braga.
              </p>
              <span className="text-xs uppercase tracking-widest text-gray-500">Futsal Feminino</span>
            </div>
            <div className="border border-white/10 p-8 hover:border-white/30 transition-colors">
              <h3 className="font-heading text-2xl font-bold mb-4">GVERREIROS DO FUTURO</h3>
              <p className="text-gray-400 text-sm mb-4">
                Projeto de formação em parceria com o SC Braga, com cerca de 80 crianças a treinar e competir.
              </p>
              <span className="text-xs uppercase tracking-widest text-gray-500">Formação</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FootballPage;
